import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, TextField } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEventListener from '../../hooks/useEventListener.hooks';
import useIssueFlagWindow from '../../hooks/issueReport/useIssueFlagWindow.hooks';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { ISSUE_DETAIL_CHAR_CAP } from '../../redux/features/issueReport/issueReport.slice';
import {
  ASR_INCORRECT_ISSUE_TYPE,
  ASR_MISSING_ISSUE_TYPE,
} from '../../constants/issueTypes';
import Colors from '../../utils/color';
import { focusOmniBar } from '../../utils/omniBarFocus';
import {
  HITL3_FONT_SIZE,
  HITL3_TITLE_FONT_SIZE,
} from '../../utils/hitl3Typography';
import FlagWindowDial from './FlagWindowDial';
import IssueTypeChips from './IssueTypeChips';
import PriorFlagsLine from './PriorFlagsLine';
import { ISSUE_PANEL_Z_INDEX } from './issueReportLayers';

/**
 * HITL-1501: confirmation that a flag landed, the Undo window, and the ONLY chance to
 * classify it.
 *
 * There is no header chip, no modal and no task-router surface — a report not
 * classified here stays `Unclassified` permanently. That is why the window is 10s
 * rather than 5s, why the detail field pauses the clock, and why the whole taxonomy
 * is on screen at once instead of behind a drill-in.
 *
 * ## Not a FloatingBanner
 *
 * That component is a one-line flex row in a fixed slot shared with RefreshBanner and
 * TaskRouterOnBanner. Growing it into a panel for one caller would drift the other
 * two, so this owns its own geometry — while deliberately keeping the family's slot
 * (`top: 100px`, centred, daintree), because that is where the agent's eye already
 * goes for a screen-level notice.
 *
 * ## Non-modal by construction
 *
 * Absolutely positioned, no MUI Modal, no backdrop, no focus trap, and nothing is
 * autofocused. No ordering control is ever blocked or delayed.
 *
 * `data-issue-report="true"` sits on the ROOT and that is load-bearing: the
 * agent-interception guards listen on `document` in the CAPTURE phase and match with
 * `target.closest()`, so the marker must be an ANCESTOR of the real event target —
 * a chip's inner text node, the icon svg, the input. Marking anything deeper would
 * pop the "would you like to take over?" dialog on every classification click and
 * every keystroke in the detail field.
 *
 * Undo emits a RETRACTION at the next revision; it does not cancel the revision-0
 * send. Committing on press is the point — a refresh, a car exit or a network blip
 * inside the window would otherwise lose the flag, and blips are exactly what agents
 * flag.
 */
const IssueFlagPanel = () => {
  const {
    commitIssueType,
    voidIssue,
    pauseFlagWindow,
    resumeFlagWindow,
    closeFlagWindow,
    priorSessionReports,
    activeReport,
  } = useIssueReport();
  const flagWindow = useIssueFlagWindow();
  const [detail, setDetail] = useState('');
  const detailRef = useRef<HTMLInputElement>(null);

  // The detail the agent typed must survive the unmount that closing causes, so the
  // timeout commit reads a ref rather than state torn down mid-effect.
  const detailValueRef = useRef('');
  detailValueRef.current = detail;
  // The clock is paused ONCE, on the first keystroke — not on focus, and not per
  // keystroke. Focus cannot be the trigger any more: the field is focused
  // automatically when the panel opens, so pausing on focus would mean the countdown
  // never ran and every panel lived to the 60s hard cap. Per-keystroke would be a
  // second clock racing the first, and the dial would stutter as the agent thinks.
  const pausedRef = useRef(false);

  const issueId = flagWindow?.issueId ?? null;
  // The Escape listener is registered unconditionally (hooks cannot be conditional),
  // so it reads the live id from a ref rather than closing over a stale one.
  const flagWindowRef = useRef<string | null>(null);
  flagWindowRef.current = issueId;
  // Set once a chip has already committed this window's detail, so the close
  // transition below does not send it a second time at the next revision.
  const committedRef = useRef(false);
  /**
   * The detail this window OPENED with, when the gesture that filed it seeded one
   * (the word-click prefills `"burger" should be `).
   *
   * Held so the close transition can tell a seed the agent never finished from text
   * they actually wrote. Without it every timed-out word-click would emit a second
   * revision carrying a sentence the agent did not type.
   */
  const seededDetailRef = useRef('');
  /**
   * The type the record already carries, read by the close transition.
   *
   * That transition commits a typed detail with the type alongside it, and
   * `reportAmended` assigns the type unconditionally — so passing null there would
   * ERASE a classification the gesture had already made. A word click files
   * `Incorrect Transcription`; without this, finishing the sentence wiped it.
   *
   * Captured when the window OPENS, not during render: closing nulls
   * `activeReportId`, so the last render before unmount has no active report and a
   * render-time assignment would put back the null this exists to prevent. A chip
   * click cannot be missed either — it sets `committedRef` and the cleanup returns
   * before reaching this.
   */
  const activeTypeRef = useRef<string | null>(null);

  /**
   * One window, one detail. Resetting per window id rather than on unmount, because
   * this component never unmounts — it renders null between flags, so without this
   * the next flag would open showing the previous agent's text.
   *
   * The cleanup is also where a TIMED-OUT detail gets recorded: the agent typed
   * something and the clock ran out before they picked a chip. Discarding it would
   * throw away the only signal that says what the taxonomy is missing.
   */
  useEffect(() => {
    // Seeded from the record, not from a prop: the report is already filed by the
    // time this panel opens, so the record is the one source for what it carries.
    // A trailing space is added for CONTINUATION: the record stores the sanitized
    // sentence (`"burger" should be`, trimmed for the wire), but the agent is meant
    // to type the next word onto the end of it, and the caret must not land flush
    // against `be`. The unchanged-seed comparison below trims both sides, so this
    // cannot make an untouched seed look edited.
    const stored = activeReport?.issue_detail ?? '';
    const seed = stored ? `${stored} ` : '';
    seededDetailRef.current = seed;
    activeTypeRef.current = activeReport?.issue_type ?? null;
    setDetail(seed);
    committedRef.current = false;
    pausedRef.current = false;
    if (!issueId) return undefined;
    // EVERY window takes the caret, seeded or not.
    //
    // Review asked for this to be gated on a seed — focus the field for a transcript
    // gesture, where there is a half-written sentence to finish, and leave the caret
    // alone for an action-bar flag, so an agent who flags and keeps taking the order
    // does not send order text into `issue_detail`. Implemented, and it was worse:
    //
    //  1. `FlagIssueButton` goes `disabled` the moment the window opens, so the
    //     browser blurs it and `document.activeElement` becomes `<body>`.
    //  2. The omnibar reclaims the caret on its next render, because `<body>` is not
    //     inside a marked subtree.
    //  3. The agent, looking at a panel with a text field, types — into the OMNIBAR.
    //     An Enter there speaks their bug report to the customer.
    //
    // So the two failures are not symmetric. Not focusing risks issue text reaching
    // the customer; focusing risks order text landing in `issue_detail`, which is
    // noise in a report and never leaves the building. The reviewer's scenario also
    // needs the agent to type blind: the field is on screen, focused, filling up in
    // front of them. Taking the caret is the safer default, so it stays.
    //
    // Focused explicitly rather than via the `autoFocus` prop, and on a timeout: the
    // field has to exist and the surrounding DOM settle before it can take the caret.
    // (The omnibar no longer refocuses on every render — it is handed the caret at
    // boundaries instead — but the panel still has to claim the field after mount, and
    // the timeout is also what puts this AFTER the click handback that opened it.)
    // Nothing takes it back while the panel is up: data-caret-owner says so.
    const focusTimer = setTimeout(() => {
      const field = detailRef.current;
      if (!field) return;
      field.focus();
      // Caret AFTER the seed, so the agent completes the sentence rather than
      // landing in front of it or having to press End first. An unseeded window puts
      // it at 0, which is the same thing.
      const end = field.value.length;
      field.setSelectionRange(end, end);
    }, 0);
    return () => {
      clearTimeout(focusTimer);
      // Hand the caret straight back, so the agent can type the order the instant
      // the panel goes. Without this it sits on <body> until some unrelated
      // re-render happens to run the omnibar's own refocus.
      //
      // Deferred a tick, symmetrically with how it is claimed above: the panel's DOM
      // is torn down around this cleanup, and focusing while the currently-focused
      // node is being removed leaves the caret on <body> anyway.
      setTimeout(focusOmniBar, 0);
      const typed = detailValueRef.current.trim();
      if (!typed || committedRef.current) return;
      // Unchanged seed is not agent input. Compared trimmed, because the seed ends
      // in a space and `sanitizeIssueDetail` would trim it away anyway — so an
      // untouched prefill and its sanitized form must compare equal.
      if (typed === seededDetailRef.current.trim()) return;
      commitIssueType({
        issue_id: issueId,
        // Preserved, not nulled — see activeTypeRef.
        issue_type: activeTypeRef.current,
        issue_detail: typed,
      });
    };
    // commitIssueType is stable across renders (useCallback in useIssueReport).
    // Keyed on the window id ALONE: re-running when the record's own issue_detail
    // changes would reset the field out from under the agent on every amend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  /**
   * Esc is TWO-STAGE. With focus in the detail field it blurs and resumes the clock,
   * leaving the panel up; only a second press closes. A single Esc that destroyed a
   * half-typed detail would be the wrong default next to the ordering controls.
   *
   * WINDOW CAPTURE, not a React onKeyDown on the panel. A React handler only fires
   * when focus is already inside the panel, and it almost never is: the omnibar
   * refocuses itself on every render, so Escape would be delivered there and the
   * panel could never be dismissed by keyboard. Window capture also runs before
   * Autocomplete's own `clearOnEscape`, so dismissing the panel cannot wipe a
   * half-typed order.
   *
   * Because it captures on `window` and stops propagation, it would otherwise hold
   * Escape hostage for as long as the panel is open — including from an unrelated
   * modal that happens to be up (the take-over prompt). So it stands down whenever a
   * dialog is in the DOM, the same check `useIntentHotkeys` and `LiveTranscriptCard`
   * already make. The panel is `role="region"`, never a dialog, so this cannot match
   * the panel itself; and the panel still closes on its own clock, so standing down
   * cannot strand it.
   */
  const handleEscape = (event: Event) => {
    const e = event as KeyboardEvent;
    if (e.key !== 'Escape' || !flagWindowRef.current) return;
    if (
      typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]')
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (document.activeElement === detailRef.current) {
      detailRef.current?.blur();
      return;
    }
    pausedRef.current = false;
    // Closing with text typed still records it — see the timeout path in
    // useIssueFlagWindow. A typed detail is an explicit agent action.
    // The close transition records a typed detail, so this only has to close.
    closeFlagWindow();
  };

  useEventListener('keydown', handleEscape, window, true);

  /**
   * Every chip behaves identically — commit and close, detail optional. There is no
   * blocking branch anywhere, including for `Not listed`: a confirm step buys nothing
   * when re-classifying is just another revision.
   */
  const handleSelect = useCallback(
    (issueType: string) => {
      const id = flagWindowRef.current;
      if (!id) return;
      committedRef.current = true;
      commitIssueType({
        issue_id: id,
        issue_type: issueType,
        issue_detail: detailValueRef.current.trim() || null,
      });
      // The thunk deliberately does not close the window — it is also used by the
      // timeout path, where the window has already gone. Closing is the caller's job,
      // and a chip click must close IMMEDIATELY rather than wait out the countdown.
      closeFlagWindow();
    },
    [commitIssueType, closeFlagWindow]
  );

  const handleDetailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!pausedRef.current) {
        pausedRef.current = true;
        pauseFlagWindow();
      }
      setDetail(e.target.value);
    },
    [pauseFlagWindow]
  );

  /**
   * Enter submits — but only on the two ASR types.
   *
   * Those are the reports that arrive already classified from a transcript gesture,
   * with the caret sitting in the detail field: the agent's only remaining job is to
   * finish the sentence, so making them reach for a chip afterwards is a step that
   * buys nothing.
   *
   * NOT unconditional, and that is the whole safety argument. On an action-bar flag
   * no type has been chosen yet, so an Enter pressed out of habit would commit
   * `issue_type: null` — and Unclassified is TERMINAL. A reflex keystroke would
   * permanently destroy the classification on the most common path. Enter therefore
   * does nothing at all unless a type is already on the record.
   *
   * Keyed on the ASR values rather than on "a type is set". Identical today, since a
   * chip click commits and closes on the spot so no other route leaves the panel open
   * with a type; a future gesture that pre-classifies would need adding here
   * deliberately, which is the right default for a key that sends.
   */
  const handleDetailKeyDown = useCallback(
    // Typed against the TextField's ROOT, which MUI renders as a div.
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      // Mid-composition Enter commits the IME candidate, not the report.
      if ((e.nativeEvent as unknown as { isComposing?: boolean }).isComposing) {
        return;
      }
      const id = flagWindowRef.current;
      const issueType = activeTypeRef.current;
      if (
        !id ||
        (issueType !== ASR_MISSING_ISSUE_TYPE &&
          issueType !== ASR_INCORRECT_ISSUE_TYPE)
      ) {
        return;
      }
      e.preventDefault();

      const typed = detailValueRef.current.trim();
      // An untouched seed is not agent input — revision 0 already carries it, so
      // sending would emit a revision identical to the one before it. Enter always
      // CLOSES; it only SENDS when there is something new.
      if (typed && typed !== seededDetailRef.current.trim()) {
        committedRef.current = true;
        commitIssueType({
          issue_id: id,
          // Preserved, not re-derived: the gesture already chose it.
          issue_type: issueType,
          issue_detail: typed,
        });
      }
      closeFlagWindow();
    },
    [commitIssueType, closeFlagWindow]
  );

  const handleDetailBlur = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    resumeFlagWindow();
  }, [resumeFlagWindow]);

  const handleUndo = useCallback(() => {
    const id = flagWindowRef.current;
    if (!id) return;
    // Undo retracts the whole report, so a typed detail must not be re-sent by the
    // close transition afterwards.
    committedRef.current = true;
    voidIssue(id);
  }, [voidIssue]);

  // AFTER every hook — an early return above any of them changes the hook count
  // between renders, which React rejects outright.
  if (!flagWindow) return null;

  return (
    <Box
      data-issue-report="true"
      // Declares this panel the caret owner for as long as it is open, so the
      // omnibar's click handback stands down underneath it. Cannot be inferred:
      // the panel is role="region" (no focus trap for a dialog check to find), and
      // clicking its heading leaves activeElement on <body>. See
      // CARET_OWNER_SELECTOR.
      data-caret-owner="true"
      data-testid="issue-flag-panel"
      role="region"
      aria-label="Issue flagged for this order"
      sx={{
        position: 'absolute',
        top: '100px',
        left: '50%',
        transform: 'translateX(-50%)',
        // 860, not the 760 this shipped with: at 760 the chip rows had 628px of
        // usable width after the 16px padding either side, the 92px category label
        // column and its 8px gap — and AI RESPONSE needs about 704px for its five
        // values, so it wrapped to a second row. 860 gives 728px, which clears it
        // and leaves AI ORDER (the next longest, ~608px) comfortable.
        //
        // Widened rather than tightening the chips: their padding is the touch
        // target, and the label column cannot shrink because "AI Escalation" already
        // nearly fills it. The `calc` arm still governs below ~890px viewports,
        // where wrapping resumes as graceful degradation.
        width: 'min(860px, calc(100% - 32px))',
        backgroundColor: Colors.daintree,
        color: Colors.white,
        borderLeft: `4px solid ${Colors.green}`,
        p: 2,
        zIndex: ISSUE_PANEL_Z_INDEX,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: HITL3_TITLE_FONT_SIZE,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: Colors.green }} />
            Issue flagged for this order
          </Box>

          {/* Between the title and the detail field: context is read before the
              field takes the caret, and it must not sit below the chips where the
              agent would only find it after already choosing. Renders nothing on the
              first flag of a session. */}
          <PriorFlagsLine reports={priorSessionReports} />

          {/* Directly under the title, ABOVE the chips: with one global escape hatch
              this is the only thing that says WHAT the taxonomy missed, and the flow
              reads top-to-bottom — type the context, then click the type that
              sends it. */}
          <TextField
            inputRef={detailRef}
            value={detail}
            onChange={handleDetailChange}
            onKeyDown={handleDetailKeyDown}
            // Typing PAUSES the countdown; blur resumes from the banked remainder.
            // Not "typing extends": extension has no exit when an agent is pulled
            // into an order mid-sentence.
            onBlur={handleDetailBlur}
            placeholder="Add a few more details (optional)"
            fullWidth
            size="small"
            inputProps={{ maxLength: ISSUE_DETAIL_CHAR_CAP }}
            sx={{
              mt: 1.25,
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: Colors.white,
                fontSize: HITL3_FONT_SIZE,
              },
              '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              // A clear focused border: this field now takes the caret on open, so
              // the agent must be able to see at a glance where their keystrokes are
              // going — the omnibar is inches away.
              '& .Mui-focused fieldset': {
                borderColor: `${Colors.green} !important`,
                borderWidth: '2px !important',
              },
            }}
          />
        </Box>

        <FlagWindowDial
          expiresAt={flagWindow.expiresAt}
          remainingMs={flagWindow.remainingMs}
          isPaused={flagWindow.isPaused}
          onUndo={handleUndo}
        />
      </Box>

      {/* No height cap and no scroll: all 23 chips are meant to be readable in one
          glance, and a scrollbar would hide part of the taxonomy behind a gesture —
          which is the exact failure (a value buried below the fold) this panel
          exists to fix.

          One row per category is part of that: a wrapped row reads as two groups and
          costs the height a scrollbar would have saved. See the width note above. */}
      <Box sx={{ mt: 1.5 }}>
        <IssueTypeChips
          onSelect={handleSelect}
          selectedValue={activeReport?.issue_type ?? null}
        />
      </Box>
    </Box>
  );
};

export default IssueFlagPanel;
